
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Category } from '@/types/category';

interface EditCategoryDialogProps {
    category: Category | null;
    onClose: () => void;
}

const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
};

const EditCategoryDialog: React.FC<EditCategoryDialogProps> = ({ category, onClose }) => {
    const [editingCategory, setEditingCategory] = useState<Category | null>(category);
    const queryClient = useQueryClient();

    useEffect(() => {
        setEditingCategory(category);
    }, [category]);

    const updateCategoryMutation = useMutation({
        mutationFn: async (cat: Category) => {
          console.log('Updating category with data:', cat);
          const { error } = await supabase
            .from('categories')
            .update({
              name: cat.name,
              slug: cat.slug,
              icon: cat.icon,
              color: cat.color,
              description: cat.description,
              seo_title: cat.seo_title,
              seo_description: cat.seo_description,
              meta_keywords: cat.meta_keywords,
            })
            .eq('id', cat.id);
          if (error) {
            console.error('Update error:', error);
            throw error;
          }
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
          queryClient.invalidateQueries({ queryKey: ['categories'] });
          toast.success('Category updated successfully');
          onClose();
        },
        onError: (error: any) => {
          console.error('Update category error:', error);
          toast.error(`Failed to update category: ${error.message || 'Unknown error'}`);
        },
    });

    const handleUpdateCategory = () => {
        if (!editingCategory) {
          console.log('No editing category found');
          return;
        }
        
        if (!editingCategory.name || !editingCategory.slug) {
          toast.error('Name and slug are required');
          return;
        }
        
        console.log('Attempting to update category:', editingCategory);
        updateCategoryMutation.mutate(editingCategory);
    };

    return (
        <Dialog open={!!category} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Edit Category</DialogTitle>
                </DialogHeader>
                {editingCategory && (
                    <div className="space-y-6 mt-4">
                        {/* Basic Information */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">Basic Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-name">Name *</Label>
                                    <Input
                                        id="edit-name"
                                        value={editingCategory.name}
                                        onChange={(e) => {
                                            const name = e.target.value;
                                            setEditingCategory(prev => prev ? {
                                                ...prev,
                                                name,
                                                slug: generateSlug(name)
                                            } : null);
                                        }}
                                        placeholder="Category name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-slug">Slug *</Label>
                                    <Input
                                        id="edit-slug"
                                        value={editingCategory.slug}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, slug: e.target.value } : null)}
                                        placeholder="category-slug"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-icon">Icon</Label>
                                    <Input
                                        id="edit-icon"
                                        value={editingCategory.icon || ''}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, icon: e.target.value } : null)}
                                        placeholder="🎮"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-color">Color</Label>
                                    <Input
                                        id="edit-color"
                                        type="color"
                                        value={editingCategory.color}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, color: e.target.value } : null)}
                                    />
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <Label htmlFor="edit-description">Description</Label>
                                    <Textarea
                                        id="edit-description"
                                        value={editingCategory.description || ''}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, description: e.target.value } : null)}
                                        placeholder="Category description for users"
                                        rows={3}
                                    />
                                    <p className="text-sm text-gray-500">
                                        This description will be shown to users browsing the category
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Separator />

                        {/* SEO Settings */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">SEO Settings</h3>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-seo-title">SEO Title</Label>
                                    <Input
                                        id="edit-seo-title"
                                        value={editingCategory.seo_title || ''}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, seo_title: e.target.value } : null)}
                                        placeholder="Custom SEO title for this category"
                                    />
                                    <p className="text-sm text-gray-500">
                                        If empty, will use the category name as the title
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-seo-description">SEO Description</Label>
                                    <Textarea
                                        id="edit-seo-description"
                                        value={editingCategory.seo_description || ''}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, seo_description: e.target.value } : null)}
                                        placeholder="Custom SEO description for search engines"
                                        rows={3}
                                    />
                                    <p className="text-sm text-gray-500">
                                        This description is for search engines. If empty, will use the category description above
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-meta-keywords">Meta Keywords</Label>
                                    <Input
                                        id="edit-meta-keywords"
                                        value={editingCategory.meta_keywords || ''}
                                        onChange={(e) => setEditingCategory(prev => prev ? { ...prev, meta_keywords: e.target.value } : null)}
                                        placeholder="keyword1, keyword2, keyword3"
                                    />
                                    <p className="text-sm text-gray-500">
                                        Comma-separated keywords for SEO (optional)
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex gap-2 mt-6">
                    <Button
                        onClick={handleUpdateCategory}
                        disabled={updateCategoryMutation.isPending}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {updateCategoryMutation.isPending ? 'Updating...' : 'Update Category'}
                    </Button>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditCategoryDialog;
